import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { FilterName } from "../../../modules/filter/shared/enums/filter-name";
import {
  IFilters,
  emptyFilters,
} from "../../../modules/filter/shared/interfaces/filters-state";
import { initialPagination } from "../../../modules/filter/shared/utils/types";
import { NotificationSeverity } from "../../../modules/notifications/shared/enums/notification-severity";
import { NotificationType } from "../../../modules/notifications/shared/enums/notification-type";
import { addNotificationAction } from "../../../modules/notifications/store/notifications.slice";
import type { OptionalDateRange } from "../../../shared/interface";
import { RootState } from "../../../store-types";
import { ITechOperationDto } from "../../tech-operations/shared/dtos/tech-operation.dto";
import { TFarmLandTechRequest } from "../../tech-operations/shared/interfaces/farm-land-tech-request";
import { TechOperation } from "../../tech-operations/shared/models/tech-operation";
import { techopsApiSlice } from "../../tech-operations/store/techop.rtkq";
import { IFarmLandsTechState } from "../shared/interfaces/farm-lands-tech-state";

import { FARM_LANDS_TECH_MODULE_NAME } from "./constants";
import { getFarmLandsTechState } from "./farm-lands-tech.selector";

export const FARM_LANDS_TECH_REFRESH_FAILED_MESSAGE =
  "Не удалось обновить список агроработ, показаны ранее загруженные данные";
export const FARM_LANDS_TECH_LOAD_FAILED_MESSAGE =
  "Не удалось загрузить список агроработ";

export const farmLandsTechSlice = createSlice({
  name: FARM_LANDS_TECH_MODULE_NAME,
  initialState: () => {
    const initial: IFarmLandsTechState = {
      isLoading: false,
      list: [],
      noPredecessorsList: [],
      removeSplittedFields: false,
      listCount: 0,
      filters: emptyFilters,
      pagination: { ...initialPagination },
    };
    return initial;
  },
  reducers: {
    setListAction(state, action): void {
      state.list = action.payload;
    },
    setListCountAction(state, action): void {
      state.listCount = action.payload;
    },
    setPredecessorsList(state, action: PayloadAction<TechOperation[]>): void {
      state.noPredecessorsList = action.payload;
    },
    setRemoveSplittedFields(state, action: PayloadAction<boolean>): void {
      state.removeSplittedFields = action.payload;
    },
    setFilterDateRangeAction(state, action: PayloadAction<OptionalDateRange>): void {
      const newFromTo = action.payload;
      const millisOrNull: Partial<IFilters> = {
        [FilterName.StartDate]: newFromTo.from?.getTime() || null,
        [FilterName.EndDate]: newFromTo.to?.getTime() || null,
      };
      const filtersMerged: IFilters = { ...state.filters, ...millisOrNull };
      state.filters = filtersMerged;
    },
    setPagination(state, action): void {
      state.pagination = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchFarmLandsTechAction.fulfilled, (state, _) => {
        state.isLoading = false;
      })
      .addCase(fetchFarmLandsTechAction.pending, (state, _) => {
        state.isLoading = true;
      })
      .addCase(fetchFarmLandsTechAction.rejected, (state, _) => {
        state.isLoading = false;
      });
  },
});

export const farmLandsTechReducer = farmLandsTechSlice.reducer;
export const {
  setListAction,
  setListCountAction,
  setFilterDateRangeAction,
  setPagination,
  setPredecessorsList,
  setRemoveSplittedFields,
} = farmLandsTechSlice.actions;

// async actions
export const fetchFarmLandsTechAction = createAsyncThunk<
  void,
  { request: TFarmLandTechRequest },
  { state: RootState }
>(
  `${FARM_LANDS_TECH_MODULE_NAME}/fetchFarmLandsTech`,
  async ({ request }, { dispatch, getState }) => {
    try {
      const count = await dispatch(
        techopsApiSlice.endpoints.fetchFarmLandsTechCount.initiate(request, {
          subscribe: false,
          forceRefetch: true,
        })
      ).unwrap();
      const dtos: ITechOperationDto[] = await dispatch(
        techopsApiSlice.endpoints.fetchFarmLandsTech.initiate(request, {
          subscribe: false,
          forceRefetch: true,
        })
      ).unwrap();

      const models: TechOperation[] = dtos.map((dto) => {
        const model = new TechOperation(dto.id);
        model.updateFromDto(dto);
        return model;
      });
      dispatch(setListAction(models));

      const noPredecessors = models.filter((x) => x.isOriginal);
      dispatch(setPredecessorsList(noPredecessors));

      // TODO: depends of optimisation needs, we could change it only if filter changes
      dispatch(setListCountAction(count));
    } catch (error) {
      const hasLoadedList = getFarmLandsTechState(getState()).list.length > 0;
      dispatch(
        addNotificationAction({
          type: NotificationType.Snack,
          severity: NotificationSeverity.Error,
          message: hasLoadedList
            ? FARM_LANDS_TECH_REFRESH_FAILED_MESSAGE
            : FARM_LANDS_TECH_LOAD_FAILED_MESSAGE,
          error,
        })
      );
      throw error;
    }
  }
);
